import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { playableBlockchainRarities } from '@sorare/core/src/lib/cards';

import { FixtureTopPlayersTable } from 'components/fixture/FixtureTopPlayersTable';
import { getFixtureQueryVariables } from 'lib/so5';

import type { Filters } from './TopPlayersFilters/types';
import type {
  FixtureTopPlayersQuery,
  FixtureTopPlayersQueryVariables,
} from './__generated__/index.graphql';

const FIXTURE_TOP_PLAYERS_QUERY = gql`
  query FixtureTopPlayersQuery(
    $type: So5State
    $slug: String!
    $positions: [Position!]
    $competitionSlugs: [String!]
    $myCardsRarities: [Rarity!]
    $after: String
    $rarity: Rarity = limited
    $inSeason: Boolean = true
    $onlyPrimary: Boolean = true
  ) {
    so5 {
      so5Fixture(type: $type, slug: $slug) {
        slug
        ...FixtureTopPlayersTable_so5Fixture
      }
    }
  }
  ${FixtureTopPlayersTable.fragments.so5Fixture}
` as TypedDocumentNode<FixtureTopPlayersQuery, FixtureTopPlayersQueryVariables>;

type Props = {
  eventType?: So5FixtureEvent;
  fixture: string;
  filters: Filters;
};
export const TopPlayers = ({ eventType, fixture, filters }: Props) => {
  const useIsTabletOrAbove = useIsTabletAndAbove();

  const effectiveRarities =
    filters.myCardsRarities?.length === 0
      ? [...playableBlockchainRarities]
      : filters.myCardsRarities;

  const { slug, type } = getFixtureQueryVariables(fixture);

  const { data, loading, loadMore } = usePaginatedQuery(
    FIXTURE_TOP_PLAYERS_QUERY,
    {
      variables: {
        type,
        slug: slug || fixture,
        positions:
          filters.positions?.length === 0 ? undefined : filters.positions,
        myCardsRarities: !filters.myPlayers ? undefined : effectiveRarities,
        competitionSlugs:
          filters.competitionSlugs.length === 0
            ? undefined
            : filters.competitionSlugs,
      },
      connections: ['FixturePlayerConnection'],
    }
  );

  const { so5Fixture } = data?.so5 || {};

  return (
    <FullWidthContainer>
      <Vertical gap={2}>
        <ConditionalWrapper
          Wrapper={FullWidthContainer.NoPadding}
          wrap={!useIsTabletOrAbove}
        >
          <FixtureTopPlayersTable
            eventType={eventType}
            so5Fixture={so5Fixture}
            loadMore={cursor => {
              loadMore(false, {
                after: cursor,
              });
            }}
            loading={loading}
          />
        </ConditionalWrapper>
      </Vertical>
    </FullWidthContainer>
  );
};
